<template>
  <div>
    <!-- HEADER -->
    <section class="mb-4 mb-lg-5">
      <div class="container">
        <div
          class="image-header"
          :class="{ 'rounded-regular': !authenticated }"
          :style="coverImage"
        ></div>
      </div>
    </section> <!-- END HEADER -->
    <template v-if="group">
      <!-- GROUP DATA -->
      <section class="mb-4">
        <div class="container text-center">
          <div class="col-lg-10 mx-auto">
            <h2 class="font-size-medium font-weight-bold mb-3">
              {{ group.name }}
            </h2>
            <p
              class="mt-4"
              v-if="group.description"
            >
              {{ group.description }}
            </p>
          </div>
        </div>
      </section> <!-- END GROUP DATA -->
      <!-- ACTIONS -->
      <section
        class="mb-3 mb-md-5"
      >
        <div class="container text-center d-flex flex-column flex-lg-row justify-content-center align-items-lg-center">
          <div :class="{'background-white': !nightMode, 'background-black': nightMode}" class="shadow-light rounded-xl p-2 pl-4 align-self-center d-flex align-items-center min-height-wrap">
            <div class="d-flex flex-wrap flex-md-nowrap flex-lg-wrap justify-content-center justify-content-md-between align-items-center">
              <!-- TEXT COUNT -->
              <div class="text-color2 font-size-small d-flex align-items-center mr-4">
                <span class="icon-basic mr-2">
                  <i class="fa fa-file-text-o"></i>
                </span>
                <span>{{ `${group.post_count} ${group.post_count === 1 ? 'publicação' : 'publicações'}` }}</span>
              </div> <!-- END TEXT COUNT -->
              <!-- MEMBER COUNT -->
              <div class="text-color2 font-size-small d-flex align-items-center mr-4 cursor-pointer" v-b-modal.group-members>
                <span class="icon-basic mr-2">
                  <i class="fa fa-user-o"></i>
                </span>
                <span>{{ `${group.member_count} ${group.member_count === 1 ? 'membro' : 'membros'}` }}</span>
              </div> <!-- END MEMBER COUNT -->
              <!-- BUTTONS -->
              <div class="d-flex justify-content-center flex-grow-1">
                <!-- EDIT BUTTON -->
                <router-link
                  class="btn btn-outline-color2 mt-2 mt-md-0"
                  :to="{ name: 'groups.edit', params: { slug: group.slug } }"
                  v-if="isOwner"
                >
                  Editar
                </router-link> <!-- END EDIT BUTTON -->
                <!-- JOIN BUTTON -->
                <template v-else>
                  <Button
                    type="button"
                    class="btn-outline-color2"
                    v-if="group.type === 'paid' && !group.is_member"
                    @click="subscribeGroupButtonClick"
                  >
                    Assinar
                  </Button>
                  <GroupMembershipButton
                    :group="group"
                    :isMember="group.is_member"
                    @group-membership-toggled="getGroup(false)"
                    @subscription-destroyed="getGroup(false)"
                    v-else
                  />
                </template><!-- END JOIN BUTTON -->
              </div> <!-- END BUTTONS -->
            </div>
          </div>
          <div
            class="d-flex align-items-center flex-wrap flex-md-nowrap justify-content-center mt-3 mt-lg-0"
          >
            <UserTag
              class="ml-md-2 align-self-center"
              :user="group.user"
            />
            <ShareTag
              class="mr-2 align-self-center mb-3 mb-md-0"
              type="group"
              :target="group"
            />
            <div
              class="d-block d-md-inline-block ml-2 "
              v-if="isOwner"
            >
              <Button
                class="btn-circle btn-outline-color2 d-inline-block"
                v-b-modal="`destroy-group-${group.id}`"
              >
                <i class="fa fa-trash"></i>
              </Button>
            </div>
            <div v-if="user && user.id !== group.user.id">
              <Button
                class="btn-sm btn-outline-color2 px-3 ml-2"
                v-b-modal.create-denounce
              >
                Denunciar
              </Button>
            </div>
          </div>
        </div>
        <!-- CREATE POST BUTTON -->
        <div class="container text-center mt-4" v-if="group.user.id === user.id || (group.is_public_posting_enabled && group.is_member)">
          <router-link
            class="btn btn-outline-color2"
            :to="{ name: 'groups.posts.create', params: { groupSlug: group.slug } }"
          >
            Criar nova publicação
          </router-link>
        </div> <!-- END CREATE POST BUTTON -->
      </section> <!-- END ACTIONS -->

      <GroupMembersModal
        :group="group"
        v-if="group"
      />

      <DestroyGroupModal
        :group="group"
        v-if="isOwner"
      />

      <DenounceModal
        title="Denunciar Comunidade"
        type="group"
        :target="group.id"
        v-if="group"
      >
        <template v-slot:description>
          <p class="text-center font-size-small">
            Selecione a opção que melhor se encaixa com a sua denúncia. Nossa equipe avaliará o ocorrido e tomará as medidas necessárias.
          </p>
        </template>
      </DenounceModal>

      <!-- SUBSCRIPTION STATUS -->
      <template v-if="group.is_member && group.subscription">
        <section
          class="text-center mx-auto col-md-6 mb-3"
          v-if="group.subscription.payment_status === 'waiting_payment'"
        >
          <div>
            <BaseIcon
              size="medium"
            >
              <WarningIcon />
            </BaseIcon>
          </div>
          <h2 class="font-weight-bold font-size-medium text-color2">Aguardando seu pagemento</h2>
          <p>Percebemos que sua assinatura está com um pagamento pendente. Por favor, realize até o dia <span class="font-weight-bold text-color2">{{ group.subscription.payment_expiration_date | formatDate }}</span> para evitar o cancelamento.</p>
          <router-link
            class="btn btn-sm btn-outline-color2 mt-2"
            :to="{ name: 'subscriptions.index' }"
          >
            Realizar pagamento
          </router-link>
        </section>
      </template><!-- END SUBSCRIPTION STATUS -->
      <!-- CANT ACCESS POSTS -->
      <template v-if="!canUserSeePosts">
        <!-- PAID GROUP -->
        <div
          class="mt-4 mt-lg-5"
          v-if="group && group.type === 'paid'"
        >
          <!-- GROUP WAITING PAYMENT -->
          <div
            class="mt-4 mb-5 mx-auto col-md-6 text-center"
            v-if="group.subscription && group.subscription.payment_status === 'waiting_payment'"
          >
            <div>
              <BaseIcon
                size="medium"
              >
                <LockIcon />
              </BaseIcon>
            </div>
            <h2 class="font-weight-bold font-size-medium text-color2">Acesso restrito</h2>
            <p>Estamos aguardando seu pagamento! Verifique sua caixa de e-mail ou acesse <router-link :to="{ name: 'orders.index' }" class="font-weight-bold link-color2">seus pedidos</router-link> para visualizar instruções de como finalizar sua assinatura.</p>
          </div><!-- END GROUP WAITING PAYMENT -->
          <!-- SUBSCRIBE -->
          <div
            class="mt-4 mb-5 mx-auto col-md-6 text-center"
            v-else
          >
            <!-- GROUP PROCESSING PAYMENT -->
            <div v-if="(group.subscription && group.subscription.payment_status === 'processing') || justPurchased">
              <div>
                <BaseIcon
                  size="medium"
                >
                  <LockIcon />
                </BaseIcon>
              </div>
              <h2 class="font-weight-bold font-size-medium text-color2">Acesso restrito</h2>
              <p>Estamos processando seu pagamento! Em breve você receberá um aviso por e-mail com mais instruções!</p>
            </div> <!-- END GROUP PROCESSING PAYMENT -->
            <div v-else>
              <div>
                <BaseIcon
                  size="medium"
                >
                  <LockIcon />
                </BaseIcon>
              </div>
              <h2 class="font-weight-bold font-size-medium text-color2">Acesso restrito</h2>
              <p>Para ter acesso completo a essa comunidade, assine agora mesmo pelo valor de <span class="font-weight-bold text-color2">{{ group.amount/100 | formatMoney }}</span> por mês!</p>
              <Button
                type="button"
                class="btn-outline-color2"
                @click="subscribeGroupButtonClick"
              >
                Assinar
              </Button>
            </div>
            <TheCheckoutModal
              v-if="!group.subscription"
              :products="[{
                    id: group.id,
                    slug: group.slug,
                    type: 'group'
                  }]"
              :paymentMethods="group.available_payment_methods"
              @purchase-completed="purchaseCompleted"
            />
          </div> <!-- END SUBSCRIBE -->
        </div> <!-- END PAID GROUP -->
      </template> <!-- END CANT ACCESS POSTS -->
      <!-- POSTS -->
      <template>
        <section>
          <div class="container">
            <template v-if="posts.length === 0 && postsLoader">
              <Loader
                color="color2"
                size="big"
              />
            </template>
            <div class="mt-4 mt-lg-5 text-center" v-else>
              <div v-if="posts.length === 0">
                <p>Não encontramos publicações nesse grupo</p>
              </div>
              <template v-else>
                <div class="post-thumbs-listing listing-rowed items-3-per-row">
                  <ul class="list-wrap">
                    <li
                      class="list-item"
                      v-for="post in posts"
                      :key="post.id"
                    >
                      <PostThumbCard
                        :post="post"
                      />
                    </li>
                  </ul>
                </div>
                <div class="mt-3 text-center">
                  <Button
                    class="btn-outline-color2 btn-lg"
                    :loading="postsLoader"
                    @click="loadMorePosts"
                    v-if="isLoadMorePostsVisible"
                  >
                    Carregar mais
                  </Button>
                </div>
              </template>
            </div>
          </div>
        </section>
      </template><!-- END POSTS -->
    </template>
  </div>
</template>

<script>

import variables from '@/mixins/variables'
import seo from '@/mixins/seo'
import { mapState } from 'vuex'
import GroupsService from '@/modules/groups/services/groups-service'
import PostsService from '@/modules/posts/services/posts-service'

import Button from '@/components/common/Button/Button'
import DenounceModal from '@/components/denounces/DenounceModal'
import DestroyGroupModal from '@/components/groups/DestroyGroupModal'
import GroupMembershipButton from '@/components/groups/GroupMembershipButton'
import GroupMembersModal from '@/components/groups/GroupMembersModal'
import Loader from '@/components/common/Loader/Loader'
import PostThumbCard from '@/components/post/PostThumbCard'
import TheCheckoutModal from '@/components/checkout/TheCheckoutModal'
import ShareTag from '@/components/share/ShareTag.vue'
import UserTag from '@/components/user/UserTag.vue'

import BaseIcon from '@/components/common/Icon/BaseIcon'
import LockIcon from '@/components/common/Icon/LockIcon'
import WarningIcon from '@/components/common/Icon/WarningIcon'

export default {
  mixins: [variables, seo],
  components: {
    BaseIcon,
    Button,
    DenounceModal,
    DestroyGroupModal,
    GroupMembershipButton,
    GroupMembersModal,
    Loader,
    LockIcon,
    PostThumbCard,
    TheCheckoutModal,
    ShareTag,
    UserTag,
    WarningIcon
    // BaseIcon,
    // UserIcon
  },
  data () {
    return {
      group: null,
      posts: [],
      postsLoader: false,
      isLoadMorePostsVisible: true,
      postsPage: 1,
      justPurchased: false
    }
  },
  metaInfo () {
    return {
      title: this.group ? this.group.name : '',
      meta: [
        { vmid: 'description', name: 'description', content: this.group && this.group.description ? this.group.description : this.seoDefaultDescription },
        { vmid: 'og-description', name: 'og:description', content: this.group && this.group.description ? this.group.description : this.seoDefaultDescription },
        { vmid: 'og-title', name: 'og:title', content: this.group ? `${this.group.name} | ${this.seoDefaultTitleAppend}` : `${this.seoDefaultTitle} | ${this.seoDefaultTitleAppend}` },
        { vmid: 'og-image', name: 'og:image', content: this.group && this.group.has_cover ? this.group.cover : this.seoDefaultImage }
      ]
    }
  },
  created () {
    this.getGroup()
  },
  computed: {
    coverImage () {
      return {
        backgroundImage: this.group ? `url('${this.group.cover}')` : `url('${this.noCoverUrl}')`
      }
    },
    ...mapState('users', {
      user: state => state.user
    }),
    ...mapState('auth', {
      authenticated: state => state.authenticated
    }),
    ...mapState({
      nightMode: state => state.nightMode
    }),
    isOwner () {
      if (!this.user || !this.group) return false
      return this.group.user.id === this.user.id
    },
    canUserSeePosts () {
      return (this.group && ((this.group.type === 'paid' && this.group.is_member) || this.group.type === 'default'))
    }
  },
  methods: {
    async getGroup (getPosts = true) {
      try {
        const group = await GroupsService.getGroup(this.$route.params.slug)
        this.group = group
        if (getPosts) this.getPosts()
      } catch (error) {
        this.$router.push('/')
      }
    },
    async getPosts () {
      this.postsLoader = true
      try {
        const posts = await PostsService.getPosts({
          groupId: this.group.id,
          page: this.postsPage
        })
        this.posts = [...this.posts, ...posts]
        if (posts.length <= 0) this.isLoadMorePostsVisible = false
      } catch (error) {
        await this.$router.push('/')
      }
      this.postsLoader = false
    },
    loadMorePosts () {
      this.postsPage++
      this.getPosts()
    },
    subscribeGroupButtonClick () {
      if (!this.group) return false
      if (!this.authenticated) {
        this.$bvModal.show('auth-modal')
        return
      }
      this.$bvModal.show('checkout-modal')
    },
    purchaseCompleted () {
      this.group.is_processing_payment = true
      this.justPurchased = true
    }
  }
}
</script>

<style lang="scss" scoped>
.min-height-wrap {
  min-height: 56px;
}
</style>
